import { useEffect, useMemo, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { formatClock } from '../../alumni/mentorship/MentorAvailabilityPanel';
import MentorshipService, {
    MentorshipAdminBookingMode,
    MentorshipAdminBookingSlot,
    MentorshipMatchingPair,
} from './MentorshipService';

interface MentorshipScheduleSessionModalProps {
    show: boolean;
    cohortId: number | null;
    instituteId?: number | null;
    onClose: () => void;
}

const MODE_OPTIONS: { value: MentorshipAdminBookingMode; label: string }[] = [
    { value: 'ZOOM', label: 'Zoom' },
    { value: 'GOOGLE_MEET', label: 'Google Meet' },
    { value: 'TEAMS', label: 'Teams' },
    { value: 'GOOGLE_CHAT', label: 'Google Chat' },
    { value: 'PHYSICAL_MEET', label: 'Physical Meet' },
    { value: 'OTHERS', label: 'Others' },
];

const pad2 = (value: number): string => String(value).padStart(2, '0');

const currentYearMonth = (): string => {
    const now = new Date();
    return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}`;
};

const monthFromDate = (date: string): string => (date && date.length >= 7 ? date.slice(0, 7) : currentYearMonth());

const pairLabel = (pair: MentorshipMatchingPair): string => {
    const mentee = pair.mentee?.name?.trim() || 'Mentee';
    const mentor = pair.mentor?.name?.trim() || 'Mentor';
    return `Mentee: ${mentee} · Mentor: ${mentor}`;
};

const pairSearchText = (pair: MentorshipMatchingPair): string =>
    [
        pair.mentee?.name,
        pair.mentor?.name,
        pair.mentee?.email,
        pair.mentor?.email,
        pair.id,
    ]
        .map((value) => String(value || '').trim().toLowerCase())
        .join(' ');

const MentorshipScheduleSessionModal = ({
    show,
    cohortId,
    instituteId = null,
    onClose,
}: MentorshipScheduleSessionModalProps) => {
    const [isLoadingMeta, setIsLoadingMeta] = useState(false);
    const [isLoadingSlots, setIsLoadingSlots] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [slotsError, setSlotsError] = useState('');
    const [saveError, setSaveError] = useState('');
    const [saveSuccess, setSaveSuccess] = useState('');

    const [publishedPairs, setPublishedPairs] = useState<MentorshipMatchingPair[]>([]);
    const [selectedPairId, setSelectedPairId] = useState<number | null>(null);
    const [selectedDate, setSelectedDate] = useState('');
    const [slots, setSlots] = useState<MentorshipAdminBookingSlot[]>([]);
    const [selectedSlot, setSelectedSlot] = useState<MentorshipAdminBookingSlot | null>(null);

    const [topic, setTopic] = useState('');
    const [agenda, setAgenda] = useState('');
    const [mode, setMode] = useState<MentorshipAdminBookingMode>('ZOOM');
    const [sessionLink, setSessionLink] = useState('');
    const [pairQuery, setPairQuery] = useState('');

    const selectedPair = publishedPairs.find((pair) => pair.id === selectedPairId) || null;
    const filteredPairs = useMemo(() => {
        const needle = pairQuery.trim().toLowerCase();
        if (!needle) return publishedPairs;
        return publishedPairs.filter((pair) => pairSearchText(pair).includes(needle));
    }, [publishedPairs, pairQuery]);
    const month = monthFromDate(selectedDate);
    // TEMP: admin Schedule Session shows the full standard grid (available + booked).
    // Restore "open mentor slots only" by filtering `slot.state === 'available'` and hiding booked hours.
    const daySlots = useMemo(
        () =>
            slots
                .filter((slot) => slot.date === selectedDate && slot.state !== 'past')
                .sort((a, b) => a.startMinute - b.startMinute),
        [slots, selectedDate]
    );
    const availableSlots = useMemo(
        () => daySlots.filter((slot) => slot.state === 'available'),
        [daySlots]
    );

    useEffect(() => {
        if (!show || cohortId == null) {
            return;
        }
        let cancelled = false;
        setLoadError('');
        setSlotsError('');
        setSaveError('');
        setSaveSuccess('');
        setSelectedPairId(null);
        setSelectedDate('');
        setSlots([]);
        setSelectedSlot(null);
        setTopic('');
        setAgenda('');
        setMode('ZOOM');
        setSessionLink('');
        setPairQuery('');
        setIsLoadingMeta(true);

        const load = async () => {
            try {
                const pairResult = await MentorshipService.listMatchingPairs(
                    cohortId,
                    { assignmentStatus: ['Published Pairing'] },
                    instituteId
                );
                if (cancelled) return;
                const published = (pairResult.pairs || []).filter((pair) => pair.assignmentStatus === 'Published');
                setPublishedPairs(published);
                if (published.length > 0) {
                    setSelectedPairId(published[0].id);
                }
            } catch (error) {
                if (cancelled) return;
                setPublishedPairs([]);
                setLoadError(MentorshipService.getErrorMessage(error));
            } finally {
                if (!cancelled) setIsLoadingMeta(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [show, cohortId, instituteId]);

    useEffect(() => {
        if (!show || cohortId == null || selectedPairId == null) {
            setSlots([]);
            return;
        }
        let cancelled = false;
        setIsLoadingSlots(true);
        setSlotsError('');
        setSelectedSlot(null);
        const loadSlots = async () => {
            try {
                const payload = await MentorshipService.listAdminBookingSlots(
                    cohortId,
                    selectedPairId,
                    month,
                    instituteId
                );
                if (cancelled) return;
                setSlots(payload.slots || []);
            } catch (error) {
                if (cancelled) return;
                setSlots([]);
                setSlotsError(MentorshipService.getErrorMessage(error));
            } finally {
                if (!cancelled) setIsLoadingSlots(false);
            }
        };
        loadSlots();
        return () => {
            cancelled = true;
        };
    }, [show, cohortId, selectedPairId, month, instituteId]);

    const handleSave = async () => {
        if (cohortId == null || selectedPairId == null || selectedSlot == null) return;
        const trimmedTopic = topic.trim();
        if (!trimmedTopic) {
            setSaveError('Session topic is required');
            return;
        }
        setSaveError('');
        setSaveSuccess('');
        setIsSaving(true);
        try {
            await MentorshipService.createAdminPairBooking(
                cohortId,
                selectedPairId,
                {
                    date: selectedSlot.date,
                    startMinute: selectedSlot.startMinute,
                    topic: trimmedTopic,
                    agenda: agenda.trim() || null,
                    mode,
                    sessionLink: sessionLink.trim() || null,
                },
                instituteId
            );
            setSaveSuccess('Session scheduled. Mentor and mentee were emailed when delivery succeeded.');
            setTopic('');
            setAgenda('');
            setSessionLink('');
            setSelectedSlot(null);
            const payload = await MentorshipService.listAdminBookingSlots(
                cohortId,
                selectedPairId,
                month,
                instituteId
            );
            setSlots(payload.slots || []);
        } catch (error) {
            setSaveError(MentorshipService.getErrorMessage(error));
        } finally {
            setIsSaving(false);
        }
    };

    const canSave =
        cohortId != null &&
        selectedPairId != null &&
        selectedSlot != null &&
        topic.trim() !== '' &&
        !isLoadingMeta &&
        !isSaving;

    return (
        <Modal show={show} onHide={onClose} size="lg" centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title>Schedule Session</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {isLoadingMeta ? <div className="text-muted mb-3">Loading published pairs…</div> : null}
                {loadError ? <div className="alert alert-danger py-2">{loadError}</div> : null}
                {saveSuccess ? <div className="alert alert-success py-2">{saveSuccess}</div> : null}

                <div className="mb-3">
                    <label className="form-label" htmlFor="mentorship-schedule-pair-search">
                        Published pair
                    </label>
                    <input
                        id="mentorship-schedule-pair-search"
                        type="search"
                        className="form-control"
                        placeholder="Search by mentee/mentor name or email"
                        value={pairQuery}
                        disabled={isLoadingMeta || publishedPairs.length === 0}
                        onChange={(event) => setPairQuery(event.target.value)}
                    />
                    {selectedPair ? (
                        <div className="small mt-1">
                            Selected: <strong>{pairLabel(selectedPair)}</strong>
                        </div>
                    ) : null}
                    {publishedPairs.length === 0 && !isLoadingMeta ? (
                        <div className="small text-muted mt-1">
                            Scheduling is only available for Published pairs.
                        </div>
                    ) : (
                        <div
                            className="list-group mt-2"
                            style={{ maxHeight: 220, overflowY: 'auto' }}
                            role="listbox"
                            aria-label="Published pairs">
                            {filteredPairs.length === 0 ? (
                                <div className="list-group-item text-muted">No pairs match that name or email.</div>
                            ) : (
                                filteredPairs.map((pair) => {
                                    const active = pair.id === selectedPairId;
                                    const menteeEmail = pair.mentee?.email?.trim() || '';
                                    const mentorEmail = pair.mentor?.email?.trim() || '';
                                    return (
                                        <button
                                            key={pair.id}
                                            type="button"
                                            role="option"
                                            aria-selected={active}
                                            className={`list-group-item list-group-item-action ${active ? 'active' : ''}`}
                                            onClick={() => {
                                                setSelectedPairId(pair.id);
                                                setSelectedSlot(null);
                                                setSaveError('');
                                                setSaveSuccess('');
                                            }}>
                                            <div>{pairLabel(pair)}</div>
                                            <div className={`small ${active ? '' : 'text-muted'}`}>
                                                Mentee email: {menteeEmail || '—'}
                                            </div>
                                            <div className={`small ${active ? '' : 'text-muted'}`}>
                                                Mentor email: {mentorEmail || '—'}
                                            </div>
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    )}
                </div>

                <div className="mb-3">
                    <label className="form-label" htmlFor="mentorship-schedule-date">
                        Date
                    </label>
                    <input
                        id="mentorship-schedule-date"
                        type="date"
                        className="form-control"
                        value={selectedDate}
                        disabled={selectedPairId == null}
                        onChange={(event) => {
                            setSelectedDate(event.target.value);
                            setSelectedSlot(null);
                            setSaveError('');
                            setSaveSuccess('');
                        }}
                    />
                    <div className="small text-muted mt-1">
                        Asia/Kolkata (IST). Standard hours are open by default. Booked hours are shown and cannot be
                        selected.
                    </div>
                </div>

                {isLoadingSlots ? <div className="text-muted small mb-2">Loading slots…</div> : null}
                {slotsError ? <div className="alert alert-danger py-2">{slotsError}</div> : null}
                {selectedDate && !isLoadingSlots && daySlots.length === 0 && !slotsError ? (
                    <div className="alert alert-warning py-2">
                        No remaining hours on this date. All standard slots are in the past.
                    </div>
                ) : null}
                {selectedDate && !isLoadingSlots && daySlots.length > 0 && availableSlots.length === 0 && !slotsError ? (
                    <div className="alert alert-warning py-2">All remaining hours on this date are already booked.</div>
                ) : null}
                {daySlots.length > 0 ? (
                    <div className="mb-3">
                        <div className="form-label">Slot</div>
                        <div className="d-flex flex-wrap gap-2">
                            {daySlots.map((slot) => {
                                const booked = slot.state === 'booked';
                                const active =
                                    !booked &&
                                    selectedSlot != null &&
                                    selectedSlot.date === slot.date &&
                                    selectedSlot.startMinute === slot.startMinute;
                                return (
                                    <button
                                        key={`${slot.date}-${slot.startMinute}`}
                                        type="button"
                                        disabled={booked}
                                        className={`btn btn-sm ${
                                            booked
                                                ? 'btn-outline-secondary'
                                                : active
                                                  ? 'btn-success'
                                                  : 'btn-outline-secondary'
                                        }`}
                                        onClick={() => {
                                            if (booked) return;
                                            setSelectedSlot(slot);
                                            setSaveError('');
                                            setSaveSuccess('');
                                        }}>
                                        {formatClock(slot.startMinute)} · {slot.durationMinutes} min
                                        {booked ? ' · Booked' : ''}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ) : null}

                {selectedSlot ? (
                    <div className="border rounded p-3 bg-light mb-3">
                        <div className="fw-semibold mb-1">Confirm booking</div>
                        <div className="small text-secondary mb-3">
                            {selectedPair ? `${pairLabel(selectedPair)} · ` : ''}
                            {selectedSlot.date} · {formatClock(selectedSlot.startMinute)} ·{' '}
                            {selectedSlot.durationMinutes} min
                        </div>
                        <div className="mb-2">
                            <label className="form-label" htmlFor="mentorship-schedule-topic">
                                Session Topic (required)
                            </label>
                            <input
                                id="mentorship-schedule-topic"
                                className="form-control"
                                value={topic}
                                maxLength={512}
                                placeholder="e.g. Career direction · Case prep"
                                onChange={(event) => setTopic(event.target.value)}
                            />
                            <div className="small text-muted">Free-text tags as entered (word-cloud style).</div>
                        </div>
                        <div className="mb-2">
                            <label className="form-label" htmlFor="mentorship-schedule-agenda">
                                Agenda (optional)
                            </label>
                            <textarea
                                id="mentorship-schedule-agenda"
                                className="form-control"
                                rows={3}
                                placeholder="What would you like to cover?"
                                value={agenda}
                                onChange={(event) => setAgenda(event.target.value)}
                            />
                        </div>
                        <div className="mb-2">
                            <label className="form-label" htmlFor="mentorship-schedule-mode">
                                Mode
                            </label>
                            <select
                                id="mentorship-schedule-mode"
                                className="form-select"
                                value={mode}
                                onChange={(event) => setMode(event.target.value as MentorshipAdminBookingMode)}>
                                {MODE_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="mb-0">
                            <label className="form-label" htmlFor="mentorship-schedule-link">
                                Session link (optional)
                            </label>
                            <input
                                id="mentorship-schedule-link"
                                className="form-control"
                                type="url"
                                placeholder="https://"
                                value={sessionLink}
                                onChange={(event) => setSessionLink(event.target.value)}
                            />
                            <div className="small text-muted">URL shape required when provided (http/https).</div>
                        </div>
                    </div>
                ) : null}
                {saveError ? <div className="alert alert-danger py-2 mb-0">{saveError}</div> : null}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onClose} disabled={isSaving}>
                    Cancel
                </Button>
                <Button variant="primary" onClick={handleSave} disabled={!canSave}>
                    {isSaving ? 'Saving…' : 'Confirm Booking'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default MentorshipScheduleSessionModal;
